import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { switchMap, catchError, map, tap, withLatestFrom } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from 'src/app/services/user.service';
import { WebsocketService } from 'src/app/services/websocket.service';
import { AppState } from '../app.reducer';
import * as UserActions from './user.actions';
import * as AuthActions from '../auth/auth.actions';
import { currentRole, lastLoginIsToday, User } from 'src/app/model/user';
// eslint-disable-next-line max-len
import { APPLICATION_ID, LOCALSTORAGE_LANGUAGE, LOCALSTORAGE_PERSONIFIED_USER, LOCALSTORAGE_RETURN_URL, LOCALSTORAGE_USER_DATA, PRIVACY_APPLICATION_ID } from 'src/app/conf/user';
import { langFromCode } from 'src/app/model/lang';
//TODOREPLACE import { DateAdapter } from '@angular/material/core';
import { RouteService } from 'src/app/services/routes.service';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import * as NotificationsActions from '../notifications/notifications.actions';
import { PrimeNGConfig } from 'primeng/api';
import * as SiteActions from 'src/app/store/site/site.actions';
import { getAnalytics, logEvent } from 'firebase/analytics';
import packageJson from 'package.json';
import { selectCurrentLang, selectUser } from './user.selector';
import * as AnalyticsActions from 'src/app/store/analytics/analytics.actions';

@Injectable()
export class UserEffects {

    unmuteWebsocket$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.unmuteWebsocket),
        withLatestFrom(
            this.store.select('user', 'currentLang'),
            this.store.select('auth', 'tokenData')
        ),
        tap(([action, lang, tokenData]) => {
            this.websocketService.unmuteWebSocket(tokenData.access_token, lang);
        })
    ), { dispatch: false });

    muteWebsocket$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.muteWebsocket),
        tap(() => {
            this.websocketService.muteWebSocket();
        })
    ), { dispatch: false });

    getUser$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.updateCurrentUserSuccess, AuthActions.getTokenSuccess, AuthActions.getMfaTokenSuccess),
        switchMap((action) => {
            const currentTechnician: User = JSON.parse(localStorage.getItem(LOCALSTORAGE_USER_DATA));
            const currentUser: User = JSON.parse(localStorage.getItem(LOCALSTORAGE_PERSONIFIED_USER));

            if (currentTechnician && currentUser) {
                return of(UserActions.getCurrentUserSuccess({ currentUser, currentTechnician, redirect: action.redirect, redirectImpers: true }));
            } else {
                return of(UserActions.getCurrentUser({ redirect: action.redirect }));
            }
        })
    ));

    getUserLogin$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.getCurrentUserSuccess),
        withLatestFrom(this.store.select(selectUser)),
        tap(([action, currentUser]) => {
            if (!action.redirectImpers) {
                if (!lastLoginIsToday(currentUser)) {
                    this.store.dispatch(UserActions.setFePrecLogin({ userId: currentUser.Id }));
                    this.store.dispatch(AnalyticsActions.eventFirstDateLogin({ eventName: 'first_date_login', params: { page_title: 'Login' } }));
                }
            }
        })
    ), { dispatch: false });

    getCurrentUser$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.getCurrentUser),
        switchMap((action) => this.userService.getUser().pipe(
            map((resp) => {
                if (resp.Success) {
                    return UserActions.getCurrentUserSuccess({ currentUser: resp.Data, redirect: action.redirect, redirectImpers: false });
                } else {
                    return UserActions.getCurrentUserFailed({ errorMessage: resp.ErrorMessage || 'Nasty Error' });
                }
            }),
            catchError(error => {
                console.error(error);
                return of(UserActions.getCurrentUserFailed({ errorMessage: 'Bad Credentials' }));
            })
        ))
    ));

    authFailed$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.getCurrentUserFailed),
        tap((action) => {
            this.router.navigate(['/login']);
        })
    ), { dispatch: false });

    authSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.getCurrentUserSuccess),
        tap((action) => {
            if (action.redirect) {
                const role = currentRole(action.currentUser);
                const sReturnUrl = localStorage.getItem(LOCALSTORAGE_RETURN_URL);
                const goodRoute = this.routeService.correctRoute(role, !!action.currentTechnician, sReturnUrl);
                localStorage.removeItem(LOCALSTORAGE_RETURN_URL);
                this.router.navigate(goodRoute.path, { queryParams: goodRoute.queryParams });
            }
        })
    ), { dispatch: false });

    setOwned$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.getCurrentUserSuccess),
        switchMap((action => [SiteActions.setOwned({ currentUserId: action.currentUser.Id })]))
    ));

    setUserLang$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.getCurrentUserSuccess),
        withLatestFrom(
            this.store.select('user', 'currentTechnician'),
        ),
        switchMap(([action, technician]) => {
            if (action.currentUser.Language === '') {
                return [];
            }
            let lang = action.currentUser.Language;
            if (technician !== undefined) {
                lang = technician.Language;
            }
            return [UserActions.setCurrentLang({ lang: langFromCode(lang) })];
        })
    ));

    setCurrentLang$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.setCurrentLang),
        map(action => action.lang),
        tap((lang) => {
            if (lang !== undefined && lang.code !== undefined) {
                this.translate.use(lang.code);
                this.translate.get('primeng').subscribe(res => this.primengConfig.setTranslation(res)); // set prime ng components locale (eg: calendar component)
                if (localStorage != null) {
                    localStorage.setItem(LOCALSTORAGE_LANGUAGE, lang.code);
                }
                this.store.dispatch(UserActions.setCurrentLangSuccess({ lang: lang }));
            }
        })
    ), { dispatch: false });

    // setCurrentLangSuccess is not trigger just with change_language but also when webapp set language in new session load
    /* setCurrentLangSuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.setCurrentLangSuccess),
        withLatestFrom(
            this.store.select(selectUser),
        ),
        tap(([action, currentUser]) => {
            setTimeout(() => {
                if (currentUser) {
                    this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'change_language', params: {} }));
                } else {
                    this.store.dispatch(AnalyticsActions.eventNoAuth({ eventName: 'change_language', params: {} }));
                }
            }, 3000);
        }),
    ), { dispatch: false }); */

    updateUser$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.updateCurrentUser),
        map(action => action.user),
        switchMap((user) => this.userService.updateUser(user)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return UserActions.updateCurrentUserSuccess({
                            message: this.translate.instant('UPDATE_USER_SUCCESS'),
                            redirect: false,
                        });
                    } else {
                        return UserActions.updateCurrentUserFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    console.error(error);
                    return of(UserActions.updateCurrentUserFailed({ errorMessage: error.message }));
                })
            )
        )
    ));

    updateCurrentUserSuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.updateCurrentUserSuccess),
        withLatestFrom(this.store.select(selectUser), this.store.select(selectCurrentLang)),
        tap(([action, currentUser, currentLang]) => {
            const role = currentUser && currentUser.Roles?.length > 0 ? currentUser.Roles[0].Description : 'no_data';
            const langCode = currentLang ? currentLang.code : 'no_data';
            const nation = currentUser ? currentUser.CountryName.toUpperCase() : 'no_data';
            logEvent(getAnalytics(), 'change_user_data', { user_role: role, lang: langCode, nation: nation, app_version: packageJson.version });
        }),
    ), { dispatch: false });

    updateUserExitBeta$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.updateCurrentUserExitBeta),
        map(action => action.user),
        switchMap((user) => this.userService.updateUser(user)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return UserActions.updateCurrentUserExitBetaSuccess({
                            message: this.translate.instant('UPDATE_USER_SUCCESS'),
                            redirect: false,
                        });
                    } else {
                        return UserActions.updateCurrentUserExitBetaFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    console.error(error);
                    return of(UserActions.updateCurrentUserExitBetaFailed({ errorMessage: error.message }));
                })
            )
        )
    ));

    getHeadquarters$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.getHeadQuarters),
        switchMap(() => this.userService.getHeadQuarters()
            .pipe(
                map((dataRest) => {
                    if (dataRest.ErrorMessage) {
                        return UserActions.getHeadQuartersFailed({ errorMessage: dataRest.ErrorMessage || 'Nasty Error' });
                    }
                    if (!dataRest.Data) {
                        return UserActions.getHeadQuartersFailed({ errorMessage: 'Nasty Error' });
                    }
                    return UserActions.getHeadQuartersSuccess({ headquarters: dataRest.Data });
                }),
                catchError(error => {
                    console.error(error);
                    return of(UserActions.getHeadQuartersFailed({ errorMessage: 'Nasty Error' }));
                })
            ))
    ));

    confirmShare$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.confirmShareDevice),
        switchMap((action) => this.userService.confirmShareDevice(action.invitationData)
            .pipe(
                map(resp => {
                    if (resp.ErrorCode) {
                        return UserActions.confirmShareDeviceFailed({ errorMessage: resp.ErrorMessage });
                    } else {
                        return UserActions.confirmShareDeviceSuccess({
                            message: this.translate.instant('CONFIRM_SHARE_DEVICE_SUCCESS')
                        });
                    }
                }),
                catchError(error => {
                    console.error(error);
                    return of(UserActions.confirmShareDeviceFailed({ errorMessage: error.message }));
                })
            )
        )
    ));

    confirmSiteTransfer$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.confirmSiteTransfer),
        switchMap((action) => this.userService.confirmSiteTransfer(action.siteId, action.ownerId, action.verification, action.userId)
            .pipe(
                map(resp => {
                    if (resp.ErrorCode) {
                        return UserActions.confirmSiteTransferFailed({ errorMessage: resp.ErrorMessage });
                    } else {
                        return UserActions.confirmSiteTransferSuccess();
                    }
                }),
                catchError(error => {
                    console.error(error);
                    return of(UserActions.confirmSiteTransferFailed({ errorMessage: error.message }));
                })
            )
        )
    ));

    getTechnicians$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.getTechnicians),
        switchMap(() => this.userService.getUserTechnicians().pipe(
            map((techniciansData) => {
                if (techniciansData.ErrorMessage) {
                    return UserActions.getTechniciansFailed({ errorMessage: 'Nasty Error' });
                }
                if (!techniciansData.Data) {
                    return UserActions.getTechniciansFailed({ errorMessage: 'Nasty Error' });
                }
                return UserActions.getTechniciansSuccess({ technicians: techniciansData.Data });
            }),
            catchError(error => of(UserActions.getTechniciansFailed({ errorMessage: error.message })))
        )
        )
    ));

    /* get technician by technician code */
    getTechnician$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.getTechnicianByCode),
        map(action => action.code),
        switchMap((code) => this.userService.getTechnicianByCode(code)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return UserActions.getTechnicianSuccess({ technician: resp.Data });
                    } else if (resp.ErrorMessage === "invalid user tech code") { // removed toastmessage invalid user
                        return UserActions.getTechnicianFailed({ errorMessage: "" });
                    } else {
                        return UserActions.getTechnicianFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    console.error(error);
                    // eslint-disable-next-line max-len
                    return of(UserActions.getTechnicianFailed({ errorMessage: error.message }));
                })
            )
        )
    ));

    getNotificationMaintenance$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.getCurrentUserSuccess, UserActions.getNotificationMaintenance),
        withLatestFrom(
            this.store.select('user', 'currentLang'),
        ),
        switchMap(([action, currentLang]) => this.userService.getNotificationMaintenance(APPLICATION_ID, currentLang.code)
            .pipe(
                map((dataRest) => {
                    if (!dataRest.Success) {
                        // eslint-disable-next-line max-len
                        return UserActions.getNotificationMaintenanceFailed({ errorMessage: dataRest.ErrorMessage || this.translate.instant('UNEXPECTED_ERROR') });
                    }
                    return UserActions.getNotificationMaintenanceSuccess({ notificationMaintenance: dataRest.Data });
                }),
                catchError(error => {
                    console.error(error);
                    // eslint-disable-next-line max-len
                    return of(UserActions.getNotificationMaintenanceFailed({ errorMessage: this.translate.instant('UNEXPECTED_ERROR') }));
                })
            ))
    ));

    revokeShareInvitation$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.revokeShareInvitation),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        switchMap(([action, device]) => {
            const deviceId = action.deviceId ? action.deviceId : device.Id;
            return this.userService.revokeShareInvitation(action.email, deviceId).pipe(
                map(resp => {
                    if (resp.Success) {
                        return UserActions.revokeShareInvitationSuccess({
                            email: action.email,
                            message: this.translate.instant('REVOKE_INVITATION_SUCCESS'),
                        });
                    } else {
                        return UserActions.revokeShareInvitationFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(UserActions.revokeShareInvitationFailed({ errorMessage: error.message })))
            );
        })
    ));

    revokeCustomerInvitation$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.revokeCustomerInvitation),
        switchMap((action) => this.userService.revokeCustomerInvitation(action.email)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return UserActions.revokeCustomerInvitationSuccess({
                            email: action.email,
                            message: this.translate.instant('REVOKE_INVITATION_SUCCESS'),
                        });
                    } else {
                        return UserActions.revokeCustomerInvitationFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(UserActions.revokeCustomerInvitationFailed({ errorMessage: error.message })))
            )
        )
    ));

    deleteCustomer$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.deleteCustomer),
        switchMap((action) => this.userService.deleteCustomer(action.userId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return UserActions.deleteCustomerSuccess({
                            userId: action.userId,
                            message: this.translate.instant('DELETE_CUSTOMER_SUCCESS'),
                        });
                    } else {
                        return UserActions.deleteCustomerFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(UserActions.deleteCustomerFailed({ errorMessage: error.message })))
            )
        )
    ));

    closeAccount$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.closeAccount),
        switchMap((action) => this.userService.closeAccount(action.pwd)
            .pipe(
                map(resp => {
                    this.store.dispatch(NotificationsActions.hideProgressSpinner());
                    if (resp.Success) {
                        this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'close_account_confirmed', params: { page_title: 'Profile' } }));
                        return UserActions.closeAccountSuccess({
                            message: this.translate.instant('CLOSE_ACCOUNT_SUCCESS'),
                        });
                    } else {
                        if (resp.ErrorMessage === 'invalid credentials') {
                            return UserActions.closeAccountFailed({ errorMessage: this.translate.instant('CLOSE_ACCOUNT_ERR_PASSW') });
                        }
                        return UserActions.closeAccountFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    this.store.dispatch(NotificationsActions.hideProgressSpinner());
                    return of(UserActions.closeAccountFailed({ errorMessage: this.translate.instant('UNEXPECTED_ERROR_RETRY_LATER') }));
                })
            )
        )
    ));

    getCustomers$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.getCustomers),
        switchMap((action) => this.userService.getCustomers(action.paging, action.filter, action.status)
            .pipe(
                map((resp) => {
                    if (resp.Success) {
                        return UserActions.getCustomersSuccess({ paging: resp.Data.Paging, customers: resp.Data.Items });
                    } else {
                        return UserActions.getCustomersFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(UserActions.getCustomersFailed({ errorMessage: error.message })))
            )
        )
    ));

    getAllCustomers$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.getAllCustomers),
        switchMap((action) => this.userService.getAllCustomers()
            .pipe(
                map((resp) => {
                    if (resp.Success) {
                        return UserActions.getAllCustomersSuccess({ customers: resp.Data });
                    } else {
                        return UserActions.getAllCustomersFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(UserActions.getAllCustomersFailed({ errorMessage: error.message })))
            )
        )
    ));

    inviteCustomer$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.inviteCustomer),
        switchMap((action) => this.userService.inviteCustomerNew(action.email, action.firstName, action.lastName)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return UserActions.inviteCustomerSuccess({
                            message: this.translate.instant(
                                'INVITE_CUSTOMER_SUCCESS',
                                { firstName: action.firstName, lastName: action.lastName }
                            )
                        });
                    } else {
                        return UserActions.inviteCustomerFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                    }
                }),
                catchError(error => of(UserActions.inviteCustomerFailed({ errorMessage: error.message })))
            )
        )
    ));

    assingTechnician$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.assignTechnicianToUser),
        switchMap((action) => this.userService.assignTechnician(action.techCode, action.userId, action.technicianId, action.verification)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return UserActions.assignTechnicianToUserSuccess({ message: this.translate.instant('ASSIGN_TECHNICIAN_SUCCESS') });
                    } else {
                        return UserActions.assignTechnicianToUserFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(UserActions.assignTechnicianToUserFailed({ errorMessage: error.message })))
            )
        )
    ));

    unassingTechnician$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.unassignTechnicianToUser),
        switchMap((action) => this.userService.unassignTechnician(action.id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return UserActions.unassignTechnicianToUserSuccess({
                            message: this.translate.instant('UNASSIGN_TECHNICIAN_SUCCESS'),
                            id: action.id,
                        });
                    } else {
                        return UserActions.unassignTechnicianToUserFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(UserActions.unassignTechnicianToUserFailed({ errorMessage: error.message })))
            )
        )
    ));

    setFePrecLogin$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.setFePrecLogin),
        switchMap((action) => this.userService.setFePrecLogin(action.userId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return UserActions.setFePrecLoginSuccess();
                    } else {
                        return UserActions.setFePrecLoginFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    return of(UserActions.setFePrecLoginFailed({ errorMessage: error.message }));
                })
            )
        )
    ));

    /* start personification mode */
    setImpersonation$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.setImpersonation),
        tap((action) => {
            const notification = new Notification("IMPERS", this.translate.instant('IMPERSONATE_USER_SUCCESS'), NotificationLevel.Info, "");
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
            if (action.siteId) {
                this.router.navigate(['/impers/devices'], { queryParams: { siteId: action.siteId } });
            } else {
                this.router.navigate(['/impers/sites']);
            }
        }),
    ), { dispatch: false });

    /* exit personification mode */
    unsetImpersonation$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.unsetImpersonation),
        tap(() => {
            this.router.navigate(['/technician/sites-list']);
        }),
    ), { dispatch: false });

    getReleaseNoteList$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.getReleaseNoteList),
        withLatestFrom(
            this.store.select('user', 'currentLang'),
        ),
        switchMap(([action, currentLang]) => this.userService.getReleaseNoteList(APPLICATION_ID, currentLang.code)
            .pipe(
                map((dataRest) => {
                    if (!dataRest.Success) {
                        // eslint-disable-next-line max-len
                        return UserActions.getReleaseNoteListFailed({ errorMessage: dataRest.ErrorMessage || this.translate.instant('UNEXPECTED_ERROR') });
                    }
                    return UserActions.getReleaseNoteListSuccess({ releaseNoteList: dataRest.Data });
                }),
                catchError(error => {
                    console.error(error);
                    // eslint-disable-next-line max-len
                    return of(UserActions.getReleaseNoteListFailed({ errorMessage: this.translate.instant('UNEXPECTED_ERROR') }));
                })
            ))
    )); PRIVACY_APPLICATION_ID

    getPrivacyNoteList$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.getPrivacyNoteList),
        withLatestFrom(
            this.store.select('user', 'currentLang'),
        ),
        switchMap(([action, currentLang]) => this.userService.getReleaseNoteList(PRIVACY_APPLICATION_ID, currentLang.code)
            .pipe(
                map((dataRest) => {
                    if (!dataRest.Success) {
                        // eslint-disable-next-line max-len
                        return UserActions.getPrivacyNoteListFailed({ errorMessage: dataRest.ErrorMessage || this.translate.instant('UNEXPECTED_ERROR') });
                    }
                    return UserActions.getPrivacyNoteListSuccess({ privacyNoteList: dataRest.Data });
                }),
                catchError(error => {
                    console.error(error);
                    // eslint-disable-next-line max-len
                    return of(UserActions.getPrivacyNoteListFailed({ errorMessage: this.translate.instant('UNEXPECTED_ERROR') }));
                })
            ))
    ));



    setFeReleaseVersionVersion$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.setFeReleaseVersion),
        withLatestFrom(this.store.select(selectUser)),
        switchMap(([action, user]) => this.userService.setFeReleaseVersion(user.Id, action.version)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return UserActions.setFeReleaseVersionSuccess({ version: action.version });
                    } else {
                        return UserActions.setFeReleaseVersionFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    return of(UserActions.setFeReleaseVersionFailed({ errorMessage: error.message }));
                })
            )
        )
    ));



    setFePrivacyVersion$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.setFePrivacyVersion),
        withLatestFrom(this.store.select(selectUser)),
        switchMap(([action, user]) => this.userService.setFePrivacyVersion(user.Id, action.version)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return UserActions.setFePrivacyVersionSuccess({ version: action.version });
                    } else {
                        return UserActions.setFePrivacyVersionFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    return of(UserActions.setFePrivacyVersionFailed({ errorMessage: error.message }));
                })
            )
        )
    ));

    setRatingVote$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.setRatingVote),
        switchMap((action) => this.userService.setRatingVote(action.ratingValue, action.text, action.appVersion)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        setTimeout(() => {
                            this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'new_vote_rating', params: { page_title: 'Rating', rating: action.ratingValue, text: action.text } }));
                        }, 1500);
                        return UserActions.setRatingVoteSuccess({ ratingId: resp.Data.Id, message: this.translate.instant('VOTE_RATING_SUCCESS') });
                    } else {
                        return UserActions.setRatingVoteFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    return of(UserActions.setRatingVoteFailed({ errorMessage: error.message }));
                })
            )
        )
    ));

    getNextRatingVote$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.getNextRatingVote),
        switchMap(() => this.userService.getNextRatingVote()
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return UserActions.getNextRatingVoteSuccess({ askNextRatingDate: resp.Data.AskNextRatingDate });
                    } else {
                        return UserActions.getNextRatingVoteFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    return of(UserActions.getNextRatingVoteFailed({ errorMessage: error.message }));
                })
            )
        )
    ));

    notifySuccess$ = createEffect(() => this.actions$.pipe(
        ofType(
            UserActions.confirmShareDeviceSuccess,
            UserActions.revokeShareInvitationSuccess,
            UserActions.revokeCustomerInvitationSuccess,
            UserActions.deleteCustomerSuccess,
            UserActions.inviteCustomerSuccess,
            UserActions.updateCurrentUserSuccess,
            UserActions.updateCurrentUserExitBetaSuccess,
            UserActions.assignTechnicianToUserSuccess,
            UserActions.unassignTechnicianToUserSuccess,
            UserActions.closeAccountSuccess,
            UserActions.setRatingVoteSuccess,
        ),
        tap((action) => {
            if (action.message === '') {
                return;
            }
            const notification = new Notification("USER", action.message, NotificationLevel.Success, "");
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    notifyError$ = createEffect(() => this.actions$.pipe(
        ofType(
            UserActions.updateCurrentUserFailed,
            UserActions.inviteCustomerFailed,
            UserActions.deleteCustomerFailed,
            UserActions.closeAccountFailed,
            UserActions.getTechnicianFailed,
            UserActions.updateCurrentUserExitBetaFailed,
            UserActions.setRatingVoteFailed
        ),
        tap((action) => {
            if (action.errorMessage === '') {
                return;
            }
            const notification = new Notification("USER", action.errorMessage, NotificationLevel.Error, "");
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });



    constructor(
        private actions$: Actions,
        private store: Store<AppState>,
        private router: Router,
        private translate: TranslateService,
        private userService: UserService,
        private routeService: RouteService,
        private websocketService: WebsocketService,
        private primengConfig: PrimeNGConfig,
    ) { }
}

